export const ADMIN_DASHBOARD_LOCALES = ["ko", "en"] as const;
export const ADMIN_DASHBOARD_LOCALE_QUERY_PARAM = "locale";
export const ADMIN_DASHBOARD_LOCALE_STORAGE_KEY = "petlog.admin.locale";

export type AdminDashboardLocale = (typeof ADMIN_DASHBOARD_LOCALES)[number];
export type AdminDashboardLocaleSource = "query" | "storage" | "default";

export type AdminDashboardResolvedLocale = {
  locale: AdminDashboardLocale;
  source: AdminDashboardLocaleSource;
};

export type AdminDashboardLocaleResolutionOptions = {
  defaultLocale?: AdminDashboardLocale;
  search?: string;
  storage?: Pick<Storage, "getItem"> | undefined;
};

function isSupportedLocale(value: string): value is AdminDashboardLocale {
  return ADMIN_DASHBOARD_LOCALES.includes(value as AdminDashboardLocale);
}

export function normalizeAdminDashboardLocale(
  value: string | null | undefined,
): AdminDashboardLocale | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  return isSupportedLocale(normalized) ? normalized : undefined;
}

export function resolveAdminDashboardLocaleFromQuery(
  search: string | undefined,
): AdminDashboardLocale | undefined {
  if (!search) {
    return undefined;
  }

  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  return normalizeAdminDashboardLocale(params.get(ADMIN_DASHBOARD_LOCALE_QUERY_PARAM));
}

export function readStoredAdminDashboardLocale(
  storage: Pick<Storage, "getItem"> | undefined,
): AdminDashboardLocale | undefined {
  if (!storage) {
    return undefined;
  }

  try {
    return normalizeAdminDashboardLocale(storage.getItem(ADMIN_DASHBOARD_LOCALE_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

export function resolveAdminDashboardLocale(
  options: AdminDashboardLocaleResolutionOptions = {},
): AdminDashboardResolvedLocale {
  const defaultLocale = options.defaultLocale ?? "ko";
  const queryLocale = resolveAdminDashboardLocaleFromQuery(options.search);
  if (queryLocale) {
    return {
      locale: queryLocale,
      source: "query",
    };
  }

  const storedLocale = readStoredAdminDashboardLocale(options.storage);
  if (storedLocale) {
    return {
      locale: storedLocale,
      source: "storage",
    };
  }

  return {
    locale: defaultLocale,
    source: "default",
  };
}

export function persistAdminDashboardLocale(
  storage: Pick<Storage, "setItem"> | undefined,
  locale: AdminDashboardLocale,
): void {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(ADMIN_DASHBOARD_LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore storage write failures and keep the in-memory locale source as the fallback.
  }
}
